import {
  FluidContextMenuSection,
  FluidTableHeader,
  ID,
} from '@lmig/fluid-core';

import { TABLE_COLUMN_VISIBILITY_STATE } from '../../../constants/constants';
import { FluidBrowserStorage } from '../../../utils/store/local-storage.class';
import { FluidTable } from '../fluid-table';
import { COLUMN_VISIBILITY } from './filter/fluid-filter.constants';
import {
  getMutatedDataItem,
  stripFluidData,
} from './fluid-table-editable-cell-management.utils';
import { getFixedWidthHeader } from './fluid-table-templating.utils';

/**
 * Handle a change to column visibility state
 * @param host
 * @param columnState
 */
export const handleColumnVisibilityChange = (
  host,
  columnState: FluidContextMenuSection[]
) => {
  const section = columnState.find(
    (section) => section.id === COLUMN_VISIBILITY
  );

  const visibleHeaders = section.toggles
    .filter((t) => t.selected)
    .map((tog) => tog.id);

  const isVisible = (header: FluidTableHeader) => {
    return (
      visibleHeaders.indexOf(header.dataPath) !== -1 || header.label === ''
    );
  };

  host._headers = host._headers.map((h) => {
    return {
      ...h,
      visible: isVisible(h),
      splitDataPoints: h.splitDataPoints?.map((split) => ({
        ...split,
        visible: isVisible(h),
      })),
    };
  });

  host.columnVisibility.emit(host._headers);

  if (host.statefulOptions?.columnVisibility?.enabled) {
    FluidBrowserStorage.set(
      TABLE_COLUMN_VISIBILITY_STATE(host.tableId),
      host._headers.map(({ dataPath, visible }) => ({ dataPath, visible })),
      host.statefulOptions?.columnVisibility?.strategy
    );
  }
};

export const isGroupedAndShouldHideData = (header: FluidTableHeader) => {
  return header.grouped && header.grouping?.hideValueWhenGrouped;
};

export const handleCellClick = (host, dataItem, header) => {
  const payload = {
    rowId: dataItem[ID],
    dataPath: header.dataPath,
    rowData: stripFluidData({ ...getMutatedDataItem(host, dataItem) }),
  };
  host.cellClicked.emit(payload);
};

export const getFixedWidthStyle = (
  header,
  headers,
  columnAdjustments,
  allowOverflow = false
) => {
  if (header.columnWidth) {
    return { width: `${getFixedWidthHeader(header, headers)}%` };
  }

  if (!!columnAdjustments && !!columnAdjustments[header.dataPath]) {
    return {
      width: columnAdjustments[header.dataPath],
      minWidth: allowOverflow && columnAdjustments[header.dataPath],
      maxWidth: allowOverflow && columnAdjustments[header.dataPath],
    };
  }

  return {};
};

/**
 * Generate the style classes for each part of the table
 * @param host
 */
export const getTableStyles = (host: FluidTable) => {
  const wrapperClass = {
    'fluid-table-wrapper': true,
    [host.theme]: true,
    inline: host.inline,
  };

  const tableClass = {
    'fluid-table': true,
    'allow-overflow-x': host.allowOverflow || host._stickyColumn,
  };

  const tableWrapperClass = {
    'table-wrapper': true,
    loading: host.dataLoading && host.dataItems?.length === 0,
    'prevent-scroll': host.dataLoading || host._dataRefreshing,
    'with-max-height': !!host.maxHeight,
    'sticky-headers': host.stickyHeaders,
    'allow-overflow-x': host.allowOverflow,
    'sticky-column': host._stickyColumn,
  };

  const tableStyle = {
    'max-height': host.maxHeight ? `${host.maxHeight}px` : null,
    'min-height': host.minHeight ? `${host.minHeight}px` : null,
    'overflow-x': host.allowOverflow ? 'auto' : null,
  };

  const tableBodyClass = {
    loading: host.dataLoading || host._dataRefreshing,
  };

  return {
    wrapperClass,
    tableClass,
    tableWrapperClass,
    tableStyle,
    tableBodyClass,
  };
};
